/**
 * Asset Type Definitions.
 *
 * TypeScript interfaces for inventory asset API payloads and responses.
 */

export interface Asset {
  id: string;
  organization_id: string;
  sku: string;
  barcode: string;
  name: string;
  description: string | null;
  category: string;
  year: number | null;
  make: string | null;
  model: string | null;
  variant: string | null;
  serial_number: string | null;
  vin: string | null;
  lot_number: string | null;
  vendor_name: string | null;
  purchase_date: string | null;
  acquisition_notes: string | null;
  status: string;
  is_on_hold: boolean;
  hold_reason: string | null;
  hold_placed_by: string | null;
  hold_placed_at: string | null;
  hold_expected_release: string | null;
  location: string | null;
  yard_section_id: string | null;
  yard_location_id: string | null;
  purchase_price: number;
  landed_cost: number;
  total_cogs: number;
  sale_price: number | null;
  sale_date: string | null;
  sale_notes: string | null;
  purchase_order_id: string | null;
  purchase_order_number: string | null;
  sale_order_id: string | null;
  sale_order_number: string | null;
  metadata_json: Record<string, unknown> | null;
  custom_fields: Record<string, string> | null;
  images: string[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Days held, served rather than computed here (FS-015). The detail page used
   * to work it out from `created_at` alone, so every imported machine read zero
   * days in stock — the defect FS-027 fixed on the server and nothing had fixed
   * on the client. The server's clock starts at `purchase_date` and stops at
   * `sale_date`.
   */
  days_in_inventory: number;
  /**
   * Whether this status is final, from the organization's own lifecycle.
   * Replaces `status !== "Sold"`, which is wrong for a renamed lifecycle and
   * for the five other terminal statuses. Null when nothing defines the status.
   */
  status_is_terminal: boolean | null;
}

export interface AssetCreate {
  name: string;
  description?: string;
  category?: string;
  year?: number;
  make?: string;
  model?: string;
  variant?: string;
  serial_number?: string;
  vin?: string;
  lot_number?: string;
  status?: string;
  location?: string;
  purchase_price?: number;
  metadata_json?: Record<string, unknown>;
  custom_fields?: Record<string, string>;
  images?: string[];
}

export interface AssetUpdate {
  name?: string;
  description?: string;
  category?: string;
  year?: number;
  make?: string;
  model?: string;
  variant?: string;
  serial_number?: string;
  vin?: string;
  lot_number?: string;
  status?: string;
  /** Why the status is changing. Required by some statuses, and by any override. */
  status_reason?: string | null;
  /**
   * Ignore the lifecycle's allowed transitions for this change. Needs a
   * `status_reason` and is recorded as an override so it can be found later.
   */
  status_override?: boolean;
  location?: string;
  purchase_price?: number;
  metadata_json?: Record<string, unknown>;
  custom_fields?: Record<string, string>;
  images?: string[];
}

export interface AssetCostEntry {
  id: string;
  asset_id: string;
  cost_type: string;
  amount: number;
  description: string | null;
  reference_id: string | null;
  reference_type: string | null;
  created_at: string;
}

export interface AssetCostEntryCreate {
  cost_type: string;
  amount: number;
  description?: string;
}

export interface CustomStatus {
  id: string;
  organization_id: string;
  entity_type: string;
  name: string;
  color: string;
  sort_order: number;
  is_default: boolean;
  is_active: boolean;
  /** Lifecycle grouping — intake, reconditioning, selling, closed. */
  phase?: string | null;
  is_terminal?: boolean;
  created_at: string;
}

export interface BarcodeData {
  sku: string;
  barcode_base64: string;
  code_format: string;
}

/** Category options for the asset form. */
export const ASSET_CATEGORIES = [
  { value: "equipment", label: "Equipment" },
  { value: "vehicle", label: "Vehicle" },
  { value: "tractor", label: "Tractor" },
  { value: "machinery", label: "Machinery" },
  { value: "parts", label: "Parts" },
  { value: "general", label: "General" },
] as const;

/** Cost type options for cost entry forms. */
export const COST_TYPES = [
  { value: "purchase", label: "Purchase" },
  { value: "shipping", label: "Shipping" },
  { value: "freight", label: "Freight" },
  { value: "customs", label: "Customs" },
  { value: "prep", label: "Prep" },
  { value: "labor", label: "Labor" },
  { value: "parts", label: "Parts" },
  { value: "materials", label: "Materials" },
  { value: "cleaning", label: "Cleaning" },
  { value: "other", label: "Other" },
] as const;
